import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

type FetchMoreFunction<T, TT> = (
  pageCursor?: TT
) => Promise<FetchMoreResponse<T, TT>>;

interface FetchMoreResponse<T, TT> {
  nextPageCursor: TT | null | undefined;
  data: T[];
}

interface PaginationHookResult<T> {
  data: T[];
  // hack for manual control over the response data
  setData: Dispatch<SetStateAction<T[]>>;
  loading: boolean;
  shouldFetchMore: boolean;
  fetchMore: () => Promise<void>;
}

export default function usePagination<T, TT = string>(
  fetchMore: FetchMoreFunction<T, TT>
): PaginationHookResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [shouldFetchMore, setShouldFetchMore] = useState<boolean>(true);
  const pageCursor = useRef<TT | undefined>(undefined);

  const fetcher = () => {
    setLoading(true);
    return fetchMore(pageCursor.current)
      .then(({ data: $data, nextPageCursor }) => {
        if (nextPageCursor) {
          pageCursor.current = nextPageCursor;
        } else {
          setShouldFetchMore(false);
        }

        setData((d) => [...d, ...$data]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetcher();
  }, []);

  return {
    data,
    setData,
    loading,
    shouldFetchMore: Boolean(data.length && shouldFetchMore),
    fetchMore: fetcher,
  };
}
